import styled, { css } from "styled-components";

const actionButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  min-height: 54px;
  padding: 0 22px;
  border-radius: 18px;
  border: 1px solid transparent;
  font-size: 0.98rem;
  font-weight: 700;
  transition: transform 180ms ease, border-color 180ms ease, background 180ms ease,
    color 180ms ease, box-shadow 180ms ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

export const HeroSection = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 1.06fr) minmax(320px, 0.94fr);
  gap: 28px;
  align-items: start;
  padding: 58px 0 24px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
    padding-top: 42px;
  }
`;

export const HeroCopy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

export const Eyebrow = styled.span`
  display: inline-flex;
  width: fit-content;
  align-items: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #f8d29a;
  font-size: 0.82rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const Title = styled.h1`
  font-family: "Space Grotesk", sans-serif;
  font-size: 23px;
  line-height: 0.98;
  letter-spacing: -0.05em;
  max-width: 700px;
`;

export const Description = styled.p`
  max-width: 600px;
  color: var(--text-muted);
  font-size: 1.04rem;
  line-height: 1.74;
`;

export const CheckoutCard = styled.section`
  position: relative;
  overflow: hidden;
  padding: 28px;
  border-radius: 30px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(180deg, rgba(31, 31, 36, 0.94), rgba(18, 18, 21, 0.96)),
    linear-gradient(140deg, rgba(255, 138, 0, 0.08), rgba(242, 61, 127, 0.1));
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.28);
`;

export const CheckoutGlow = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 100% 0, rgba(255, 138, 0, 0.18), transparent 28%),
    radial-gradient(circle at 0 100%, rgba(242, 61, 127, 0.16), transparent 30%);
`;

export const CheckoutCardBody = styled.div`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const PriceRow = styled.div`
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
`;

export const PriceLabel = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PriceValue = styled.strong`
  display: block;
  margin-top: 8px;
  font-family: "Space Grotesk", sans-serif;
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  line-height: 0.94;
`;

export const PriceHint = styled.span`
  display: block;
  max-width: 250px;
  color: var(--text-soft);
  font-size: 0.92rem;
  line-height: 1.55;
  text-align: right;

  @media (max-width: 640px) {
    text-align: left;
  }
`;

export const CheckoutGrid = styled.section`
  display: grid;
  grid-template-columns: minmax(0, 0.92fr) minmax(280px, 0.78fr);
  gap: 22px;
  margin-top: 8px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

export const Panel = styled.div`
  padding: 26px;
  border-radius: 28px;
  background: rgba(17, 18, 22, 0.8);
  border: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 640px) {
    padding: 22px;
  }
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  flex-wrap: wrap;
`;

export const PanelEyebrow = styled.span`
  color: #f8d29a;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`;

export const PanelTitle = styled.h2`
  margin-top: 10px;
  font-size: clamp(1.45rem, 2.8vw, 2rem);
  line-height: 1.08;
`;

export const PanelText = styled.p`
  margin-top: 12px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const StatusPill = styled.span<{ $tone?: "success" | "warning" | "neutral" }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 0 14px;
  border-radius: 999px;
  font-size: 0.82rem;
  font-weight: 700;
  white-space: nowrap;
  ${({ $tone }) => {
    if ($tone === "success") {
      return `
        color: #d7ffe6;
        background: rgba(34, 197, 94, 0.16);
        border: 1px solid rgba(34, 197, 94, 0.26);
      `;
    }

    if ($tone === "warning") {
      return `
        color: #ffe7bf;
        background: rgba(255, 138, 0, 0.14);
        border: 1px solid rgba(255, 138, 0, 0.22);
      `;
    }

    return `
      color: var(--text-muted);
      background: rgba(255, 255, 255, 0.05);
      border: 1px solid rgba(255, 255, 255, 0.08);
    `;
  }}
`;

export const CheckoutForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-top: 24px;
`;

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FieldLabel = styled.label`
  color: var(--text-soft);
  font-size: 0.84rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

export const EmailInput = styled.input`
  min-height: 56px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: var(--text);
  padding: 0 16px;
  font-size: 1rem;

  &::placeholder {
    color: rgba(244, 244, 245, 0.34);
  }

  &:focus {
    outline: none;
    border-color: rgba(255, 138, 0, 0.68);
    box-shadow: 0 0 0 3px rgba(255, 138, 0, 0.14);
  }

  &[readonly] {
    color: rgba(244, 244, 245, 0.94);
    background: rgba(255, 255, 255, 0.06);
  }
`;

export const InlineNote = styled.span`
  color: var(--text-soft);
  font-size: 0.88rem;
  line-height: 1.55;
`;

export const ErrorMessage = styled.p`
  color: #ffb9c8;
  font-weight: 700;
  line-height: 1.55;
`;

export const SuccessMessage = styled.p`
  color: #d7ffe6;
  font-weight: 700;
  line-height: 1.55;
`;

export const PrimaryButton = styled.button`
  ${actionButtonStyles};
  cursor: pointer;
  background: linear-gradient(135deg, var(--accent-start), var(--accent-end));
  color: #fff;
  box-shadow: 0 18px 38px rgba(242, 61, 127, 0.18);

  &:disabled {
    cursor: wait;
    opacity: 0.72;
    transform: none;
  }
`;

export const SecondaryButton = styled.button`
  ${actionButtonStyles};
  cursor: pointer;
  color: var(--text);
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.08);

  &:disabled {
    cursor: wait;
    opacity: 0.72;
    transform: none;
  }
`;

export const SummaryStack = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 18px;
`;

export const SummaryCard = styled.div`
  padding: 16px 18px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
`;

export const SummaryLabel = styled.span`
  display: block;
  color: var(--text-soft);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

export const SummaryValue = styled.strong`
  display: block;
  margin-top: 8px;
  color: var(--text);
  font-size: 1rem;
  line-height: 1.52;
`;

export const PaymentSection = styled.div`
  display: grid;
  gap: 18px;
`;

export const SuccessState = styled.div`
  display: grid;
  gap: 18px;
  justify-items: start;
  padding: 8px 0;
`;

export const SuccessIconWrap = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px;
  height: 72px;
  border-radius: 24px;
  background: linear-gradient(135deg, rgba(34, 197, 94, 0.24), rgba(16, 185, 129, 0.18));
  border: 1px solid rgba(34, 197, 94, 0.28);
  color: #dcfce7;
  font-size: 2rem;
`;

export const SuccessTitle = styled.h3`
  font-size: 1.5rem;
  line-height: 1.08;
`;

export const SuccessText = styled.p`
  max-width: 420px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const ModalBackdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(7, 8, 10, 0.72);
  backdrop-filter: blur(8px);
`;

export const ModalCard = styled.div`
  width: min(100%, 460px);
  padding: 28px;
  border-radius: 28px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background:
    linear-gradient(180deg, rgba(31, 31, 36, 0.98), rgba(17, 18, 22, 0.98)),
    linear-gradient(140deg, rgba(255, 138, 0, 0.08), rgba(242, 61, 127, 0.1));
  box-shadow: 0 28px 60px rgba(0, 0, 0, 0.34);
`;

export const ModalTitle = styled.h3`
  margin-top: 18px;
  font-size: 1.7rem;
  line-height: 1.04;
`;

export const ModalText = styled.p`
  margin-top: 14px;
  color: var(--text-muted);
  line-height: 1.7;
`;

export const ModalActions = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

export const QrCard = styled.div`
  display: grid;
  place-items: center;
  min-height: 244px;
  padding: 18px;
  border-radius: 24px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);

  img {
    width: min(100%, 228px);
    height: auto;
    border-radius: 18px;
    background: #fff;
    padding: 10px;
  }
`;

export const CodeBlock = styled.div`
  padding: 18px;
  border-radius: 22px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
`;

export const CodeLabel = styled.strong`
  display: block;
  color: var(--text);
  font-size: 0.94rem;
`;

export const CodeValue = styled.p`
  margin-top: 12px;
  color: var(--text-muted);
  line-height: 1.7;
  word-break: break-all;
`;

export const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 14px;
`;

export const InfoList = styled.div`
  display: grid;
  gap: 12px;
  margin-top: 18px;
`;

export const InfoRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  color: var(--text-muted);
  line-height: 1.6;
`;

export const InfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  min-width: 34px;
  height: 34px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
  color: #ffd8a8;
`;
